import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  IconButton,
  VStack,
  Text,
  Textarea,
  Heading,
  useColorModeValue,
  Spinner,
  Button,
  Code,
} from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import ChatInterface from "./ChatInterface";

interface Message {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date;
}

interface ChatInterfaceProps {
  onClose: () => void;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const SyntheticDataChat: React.FC<ChatInterfaceProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text:
        "Hello! To generate the synthetic data, follow the instruction:\n" +
        'Example: "Create a synthetic Usermaster table with 10 rows of data. Each row should have Id, Name, Email, and CreatedDate."',
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  const [sessionCode, setSessionCode] = useState("string");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedQuery, setGeneratedQuery] = useState("");
  const [executionResult, setExecutionResult] = useState("Execution result will appear here...");
  const [showQueryListingPage, setShowQueryListingPage] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const userBubbleColor = useColorModeValue("blue.500", "blue.400");
  const aiBubbleColor = useColorModeValue("gray.100", "gray.700");

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(
        `${Base_apiUrl}AnalyticChat/SyntheticDataChat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            userId: 1,
            sessionCode,
            message: userMessage.text,
          }),
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.messaage || `Generated Query: ${data.response}`,
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setSessionCode(data.sessionId || "No session id received");
      setGeneratedQuery(data.response || ""); // Only set query, don't auto-execute
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeGeneratedQuery = async () => {
    if (!generatedQuery) return;

    setIsExecuting(true);
    const url = `${Base_apiUrl}chat/ExecuteGeneratedQuery?GeneratedQuery=${encodeURIComponent(
      generatedQuery
    )}&Format=2`;

    try {
      const res = await fetch(url);
      const result = await res.text();
      setExecutionResult(result);
    } catch (err) {
      setExecutionResult("Error executing query.");
    } finally {
      setIsExecuting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (showQueryListingPage) {
    return <ChatInterface onClose={() => setShowQueryListingPage(false)} />;
  }

  return (
    <Flex direction="column" w="100%" maxW="1000px" gap={4}>
      {/* Chat Messages */}
      <Box borderRadius="lg" boxShadow="md" bg={bgColor} p={4} maxH="300px" overflowY="auto">
        <Heading size="sm" mb={2}>Conversation</Heading>
        <VStack spacing={4} align="stretch">
          {messages.map((message) => (
            <Flex key={message.id} justify={message.sender === "user" ? "flex-end" : "flex-start"}>
              <Box
                bg={message.sender === "user" ? userBubbleColor : aiBubbleColor}
                color={message.sender === "user" ? "white" : "black"}
                p={3}
                borderRadius="lg"
                maxW="80%"
              >
                <Text>{message.text}</Text>
                <Text fontSize="xs" opacity={0.8} mt={1}>
                  {message.timestamp.toLocaleTimeString()}
                </Text>
              </Box>
            </Flex>
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input Area */}
      <Box borderRadius="lg" boxShadow="md" overflow="hidden" bg={bgColor}>
        <Box p={4}>
          <Flex>
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              resize="none"
              rows={1}
              mr={2}
              borderRadius="md"
            />
            <IconButton
              colorScheme="blue"
              aria-label="Send message"
              icon={<ArrowUpIcon />}
              onClick={handleSendMessage}
              isLoading={isLoading}
              alignSelf="flex-end"
            />
          </Flex>
        </Box>
      </Box>

      {/* Generated Query */}
      <Box borderRadius="lg" boxShadow="md" p={4} bg={bgColor}>
        <Heading size="sm" mb={2}>Generated Query</Heading>
        <Code
          display="block"
          whiteSpace="pre-wrap"
          p={2}
          borderRadius="md"
          fontSize="sm"
          width="100%"
          overflowX="auto"
        >
          {generatedQuery || "// No query generated yet"}
        </Code>
        <Button
          mt={2}
          colorScheme="green"
          onClick={executeGeneratedQuery}
          isDisabled={!generatedQuery}
          isLoading={isExecuting}
        >
          Execute Query
        </Button>
      </Box>

      {/* Execution Result */}
      <Box borderRadius="lg" boxShadow="md" p={4} bg={bgColor}>
        <Heading size="sm" mb={2}>Execution Result</Heading>
        {isExecuting ? (
          <Spinner size="sm" />
        ) : (
          <Code
            display="block"
            whiteSpace="pre-wrap"
            p={2}
            borderRadius="md"
            fontSize="sm"
            width="100%"
            overflowX="auto"
            maxH="300px"
          >
            {executionResult}
          </Code>
        )}
      </Box>

      <Text fontSize="sm" color="gray.600" textAlign="center">
        <Button variant="link" colorScheme="blue" onClick={() => setShowQueryListingPage(true)}>
          Click here to return to chat interface
        </Button>
      </Text>
    </Flex>
  );
};

export default SyntheticDataChat;
